import { useMemo, useState } from 'react';
import { useTable, usePagination, Column } from 'react-table';
import TableFooterNav from './TableFooterNav';

interface TableProps {
  cols: {
    Header: string | Function | JSX.Element;
    accessor: string;
    Cell?: Function | JSX.Element;
    id?: string;
    maxWidth?: number;
    minWidth?: number;
    width?: number;
  }[];
  tableData: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    currentPage: number;
    setPageSize: (v: number) => void;
    nextPage: () => void;
    previousPage: () => void;
  };
  updateTableData?: (index: number, id: number, value: string) => void;
  headerBorderAccent?: boolean;
  defaultColumn?: Partial<Column<object>>;
  height?: string;
}

const Table = ({
  cols,
  tableData,
  onRowClick,
  pagination,
  fetching,
  headerBorderAccent,
  updateTableData,
  defaultColumn,
  height='h-full',
}: TableProps) => {
  const columns = useMemo(() => cols, [cols]);
  const data = tableData;
  const [editableRowIndex, setEditableRowIndex] = useState(null);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    state,
    pageOptions,
    pageCount,
    setPageSize,
    prepareRow,
  } = useTable(
    {
      columns,
      defaultColumn,
      data,
      manualPagination: true,
      pageCount: pagination?.pageCount,
      initialState: {
        pageIndex: pagination?.page,
        pageSize: pagination?.pageSize,
      },
      updateTableData,
      editableRowIndex,
      setEditableRowIndex,
    },
    usePagination
  );
  return (
    <>
      <div className={`overflow-x-auto w-full ${height}`}>
        <table {...getTableProps()} className='divide-y divide-gray-200 w-full'>
          <thead
            className={`bg-gray-50 border-primaryColor-normal ${
              headerBorderAccent && 'border-l-4'
            }`}
          >
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((col) => (
                  <th
                    {...col.getHeaderProps({
                      style: {
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        width: col.width,
                      },
                    })}
                    className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'
                  >
                    {col.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {fetching && (
            <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <tr key={num}>
                  {Array.from(Array(columns.length).keys()).map((num) => (
                    <td className='p-6'>
                      <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
          {!fetching && (
            <tbody
              {...getTableBodyProps()}
              className='bg-white divide-y divide-gray-200'
            >
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className='hover:bg-green-50 cursor-pointer'
                    onClick={() => onRowClick && onRowClick(row.original)}
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps({
                          style: {
                            minWidth: cell.column.minWidth,
                            maxWidth: cell.column.maxWidth,
                            width: cell.column.width,
                          },
                        })}
                        className='px-6 py-4'
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {!pagination && (
        <TableFooterNav
          nextPage={nextPage}
          previousPage={previousPage}
          currentPage={state.pageIndex + 1}
          totalPage={pageOptions.length}
          perPageHandler={setPageSize}
        />
      )}
      {pagination && (
        <TableFooterNav
          nextPage={pagination.nextPage}
          previousPage={pagination.previousPage}
          currentPage={pagination.currentPage}
          totalPage={pageCount || 1}
          perPage={pagination.pageSize}
          perPageHandler={pagination.setPageSize}
        />
      )}
    </>
  );
};

export default Table;
